import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Drawer, Box, Typography, Button } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'

import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import { parseAITranscriptResponse, parseExistingAIData } from '../../utils/aiResponseParser'
import { formatTranscriptForExport } from '../../utils/transcriptFormatter'
import useAIGeneration from '../../hooks/useAIGeneration'
import { ToastNotification } from '../atoms'
import { useToast } from '../../hooks'
import { DrawerHeader } from '../DrawerComponents'
import AIProgressBar from './AIProgressBar'

import CallDetailsCard from './cards/CallDetailsCard'
import AISummaryCard from './cards/AISummaryCard'
import ConversationCard from './cards/ConversationCard'
import SentimentCard from './cards/SentimentCard'
import QAEvaluationCard from './cards/QAEvaluationCard'
import CallNotesCard from './cards/CallNotesCard'
import RatingCommentsCard from './cards/RatingCommentsCard'

import styles from './CallDrawerModern.module.scss'

export const CallDrawerModern = ({
  selectedRow,
  currentVote,
  onUpvote,
  onDownvote,
  handleCloseDrawerInternal,
  drawerOpen,
  userId
}) => {
  const { t } = useTranslation()
  const { toast, showToast, closeToast } = useToast()

  const [isDownloading, setIsDownloading] = useState(false)

  const [generatedCallIds, setGeneratedCallIds] = useState(new Set())
  const [showProgressBar, setShowProgressBar] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const {
    steps,
    isGenerating,
    isComplete,
    error: generationError,
    startGeneration,
    reset: resetGeneration,
    needsGeneration
  } = useAIGeneration()

  const currentCallId = selectedRow?.id || selectedRow?.call_id || selectedRow?.[3]

  const handleDownloadTranscript = async () => {
    if (!selectedRow || isDownloading) return

    const callId = selectedRow.id || selectedRow.call_id || selectedRow[3]
    if (!callId) {
      showToast(t('errors.transcriptDownload'), 'error')
      return
    }

    setIsDownloading(true)

    try {
      let transcriptText = null

      if (selectedRow.ai_transcript) {
        try {
          const { transcript, isValid } = parseExistingAIData(selectedRow, 'ai_transcript')
          if (isValid && transcript) {
            transcriptText = transcript
          }
        } catch (e) {
          console.error('Failed to parse existing transcript:', e)
        }
      }

      if (!transcriptText) {
        const response = await authenticatedFetch(apiEndpoints.aiProcessCall, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ callId })
        })

        if (response.ok) {
          const data = await response.json()
          if (data.data) {
            const { transcript, isValid } = parseAITranscriptResponse(data.data)
            if (isValid && transcript) {
              transcriptText = transcript
            }
          }
        }
      }

      if (transcriptText) {
        const formattedTranscript = formatTranscriptForExport(transcriptText, selectedRow)
        const filename = `call-transcript-${callId}-${new Date().toISOString().split('T')[0]}.txt`

        const blob = new Blob([formattedTranscript], { type: 'text/plain' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } else {
        showToast('No transcript available for this call.', 'warning')
      }
    } catch (error) {
      console.error('Error downloading transcript:', error)
      showToast('Failed to download transcript. Please try again.', 'error')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleGenerationRetry = useCallback(() => {
    if (selectedRow) {
      resetGeneration()
      setShowProgressBar(true)
      startGeneration(selectedRow, () => {
        setGeneratedCallIds(prev => new Set(prev).add(currentCallId))
        setRefreshKey(prev => prev + 1)
        setTimeout(() => setShowProgressBar(false), 3000)
      })
    }
  }, [selectedRow, resetGeneration, startGeneration, currentCallId])

  useEffect(() => {
    if (drawerOpen) {
      setShowProgressBar(false)
      resetGeneration()
    }
  }, [drawerOpen, resetGeneration])

  useEffect(() => {
    if (drawerOpen && selectedRow && currentCallId) {
      if (!generatedCallIds.has(currentCallId) && needsGeneration(selectedRow)) {
        setShowProgressBar(true)
        startGeneration(selectedRow, () => {
          setGeneratedCallIds(prev => new Set(prev).add(currentCallId))
          setRefreshKey(prev => prev + 1)
          setTimeout(() => setShowProgressBar(false), 3000)
        })
      }
    }
  }, [drawerOpen, selectedRow, currentCallId, generatedCallIds, needsGeneration, startGeneration])

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={handleCloseDrawerInternal}
      disableEnforceFocus
      classes={{ paper: styles.drawerPaper }}
      ModalProps={{
        keepMounted: false,
        'aria-labelledby': 'call-drawer-title'
      }}
      PaperProps={{
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': 'call-drawer-title'
      }}
    >
      <DrawerHeader
        titleId="call-drawer-title"
        title={t('calls.drawer.title')}
        icon="phone-alt"
        subtitle={selectedRow && (
          <span className={styles.callIdBadge}>
            {t('calls.drawer.callId')} {selectedRow.id || selectedRow.call_id}
          </span>
        )}
        actions={
          <Button
            className={styles.downloadButton}
            onClick={handleDownloadTranscript}
            disabled={isDownloading}
            disableRipple
            aria-label='Download call transcript'
          >
            <FontAwesomeIcon icon="download" style={{ marginRight: 6 }} aria-hidden='true' />
            {isDownloading ? 'Downloading...' : 'Download'}
          </Button>
        }
        onClose={handleCloseDrawerInternal}
      />

      <Box className={styles.drawerContent}>
        {showProgressBar && (
          <AIProgressBar
            steps={steps}
            isGenerating={isGenerating}
            isComplete={isComplete}
            error={generationError}
            onRetry={handleGenerationRetry}
          />
        )}

        {selectedRow ? (
          <Box className={styles.cardStack}>
            <CallDetailsCard selectedRow={selectedRow} />

            <AISummaryCard selectedRow={selectedRow} key={`summary-${refreshKey}`} />

            <ConversationCard selectedRow={selectedRow} key={`conversation-${refreshKey}`} />
            <QAEvaluationCard selectedRow={selectedRow} isGenerating={isGenerating} key={`qa-${refreshKey}`} />

            <SentimentCard selectedRow={selectedRow} isGenerating={isGenerating} key={`sentiment-${refreshKey}`} />

            <CallNotesCard selectedRow={selectedRow} />
            <RatingCommentsCard
              selectedRow={selectedRow}
              currentVote={currentVote}
              onUpvote={onUpvote}
              onDownvote={onDownvote}
              userId={userId}
            />
          </Box>
        ) : (
          <Typography>{t('calls.drawer.noDetailsAvailable')}</Typography>
        )}
      </Box>
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </Drawer>
  )
}

CallDrawerModern.propTypes = {
  selectedRow: PropTypes.object,
  currentVote: PropTypes.oneOf(['up', 'down', null]),
  onUpvote: PropTypes.func,
  onDownvote: PropTypes.func,
  handleCloseDrawerInternal: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
  userId: PropTypes.string
}

export default CallDrawerModern
